import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    LayoutDashboard,
    Users,
    Calendar,
    CreditCard,
    Plane,
    Loader2,
    RefreshCw,
    BookOpen,
    GraduationCap,
    Crown,
    Sparkles,
    Check,
    X,
    Edit,
    Trash2,
    Plus,
    ExternalLink,
    Phone,
    Video,
    Gift,
    AlertCircle,
    Radio,
    PlayCircle,
    DollarSign,
    Eye,
    Clock,
    Zap,
    FileText
} from 'lucide-react';
import { toast } from 'sonner';

export default function Admin() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [users, setUsers] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [airlines, setAirlines] = useState({});
    const [refreshing, setRefreshing] = useState(false);
    
    // Edit airline modal
    const [editAirlineModal, setEditAirlineModal] = useState(false);
    const [editingAirline, setEditingAirline] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);
    
    // Add airline modal
    const [addAirlineModal, setAddAirlineModal] = useState(false);
    const [newAirline, setNewAirline] = useState({ name: '', url: '', accepting: false, note: '' });
    const [addCategory, setAddCategory] = useState('');
    
    // Search filter for airlines
    const [airlineSearch, setAirlineSearch] = useState('');

    // Live Sessions state
    const [sessions, setSessions] = useState([]);
    const [editSessionModal, setEditSessionModal] = useState(false);
    const [editingSession, setEditingSession] = useState(null);
    const [addSessionModal, setAddSessionModal] = useState(false);
    const [newSession, setNewSession] = useState({ title: '', description: '', scheduled_at: '', google_meet_link: '', weeks_count: 1 });

    // Course Videos state
    const [courseVideos, setCourseVideos] = useState([]);
    const [editVideoModal, setEditVideoModal] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [addVideoModal, setAddVideoModal] = useState(false);
    const [newVideo, setNewVideo] = useState({ module: '', title: '', description: '', youtube_id: '', duration: '' });

    // Services state
    const [services, setServices] = useState({ coaching: [], resume: [] });
    const [editServiceModal, setEditServiceModal] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [editingServiceType, setEditingServiceType] = useState(null);
    const [addServiceModal, setAddServiceModal] = useState(false);
    const [addServiceType, setAddServiceType] = useState(null);
    const [newService, setNewService] = useState({ name: '', description: '', price: '', duration: '', from_scratch_price: '', expedited_fee: '50' });
    
    // Preview state
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    // Products state
    const [products, setProducts] = useState({});
    const [editProductModal, setEditProductModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [editingProductKey, setEditingProductKey] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [statsRes, bookingsRes, usersRes, transactionsRes, airlinesRes, sessionsRes, videosRes, servicesRes, productsRes] = await Promise.all([
                api.get('/admin/stats'),
                api.get('/admin/bookings'),
                api.get('/admin/users'),
                api.get('/admin/transactions'),
                api.get('/admin/airlines'),
                api.get('/admin/sessions'),
                api.get('/admin/course-videos'),
                api.get('/admin/services'),
                api.get('/admin/products')
            ]);
            
            setStats(statsRes.data);
            setBookings(bookingsRes.data);
            setUsers(usersRes.data);
            setTransactions(transactionsRes.data);
            setAirlines(airlinesRes.data);
            setSessions(sessionsRes.data);
            setCourseVideos(videosRes.data);
            setServices(servicesRes.data);
            setProducts(productsRes.data);
        } catch (err) {
            if (err.response?.status === 403) {
                toast.error('Admin access required');
                navigate('/dashboard');
            } else {
                toast.error('Failed to load admin data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await loadData();
        setRefreshing(false);
        toast.success('Data refreshed');
    };

    const updateBookingStatus = async (bookingId, newStatus) => {
        try {
            await api.put(`/admin/bookings/${bookingId}`, { status: newStatus });
            toast.success('Booking status updated');
            loadData();
        } catch (err) {
            toast.error('Failed to update booking');
        }
    };

    const openEditAirline = (category, airline) => {
        setEditingCategory(category);
        setEditingAirline({ ...airline });
        setEditAirlineModal(true);
    };

    const saveAirlineEdit = async () => {
        try {
            await api.put(`/admin/airlines/${editingCategory}/${editingAirline.originalName || editingAirline.name}`, {
                accepting: editingAirline.accepting,
                note: editingAirline.note,
                url: editingAirline.url
            });
            toast.success('Airline updated');
            setEditAirlineModal(false);
            loadData();
        } catch (err) {
            toast.error('Failed to update airline');
        }
    };

    const deleteAirline = async (category, airlineName) => {
        if (!confirm(`Delete ${airlineName}?`)) return;
        try {
            await api.delete(`/admin/airlines/${category}/${airlineName}`);
            toast.success('Airline deleted');
            loadData();
        } catch (err) {
            toast.error('Failed to delete airline');
        }
    };

    const toggleHiringStatus = async (category, airline) => {
        try {
            await api.put(`/admin/airlines/${category}/${airline.name}`, {
                accepting: !airline.accepting,
                note: airline.note,
                url: airline.url
            });
            toast.success(`${airline.name} hiring status updated`);
            loadData();
        } catch (err) {
            toast.error('Failed to update hiring status');
        }
    };

    // Calculate airline stats
    const getAirlineStats = () => {
        let total = 0;
        let hiring = 0;
        Object.values(airlines).forEach(cat => {
            cat.airlines?.forEach(airline => {
                total++;
                if (airline.accepting) hiring++;
            });
        });
        return { total, hiring };
    };

    // Filter airlines by search
    const filterAirlines = (airlineList) => {
        if (!airlineSearch) return airlineList;
        return airlineList.filter(a => 
            a.name.toLowerCase().includes(airlineSearch.toLowerCase())
        );
    };

    const addNewAirline = async () => {
        if (!addCategory || !newAirline.name || !newAirline.url) {
            toast.error('Please fill in all required fields');
            return;
        }
        try {
            await api.post(`/admin/airlines/${addCategory}`, newAirline);
            toast.success('Airline added');
            setAddAirlineModal(false);
            setNewAirline({ name: '', url: '', accepting: false, note: '' });
            loadData();
        } catch (err) {
            toast.error('Failed to add airline');
        }
    };

    // =====================
    // Live Sessions Functions
    // =====================
    
    const createSession = async () => {
        if (!newSession.title || !newSession.scheduled_at) {
            toast.error('Please fill in title and date/time');
            return;
        }
        try {
            const res = await api.post('/admin/sessions', newSession);
            const count = res.data?.count || 1;
            toast.success(count > 1 ? `${count} weekly sessions created!` : 'Live session created!');
            setAddSessionModal(false);
            setNewSession({ title: '', description: '', scheduled_at: '', google_meet_link: '', weeks_count: 1 });
            loadData();
        } catch (err) {
            toast.error('Failed to create session');
        }
    };

    const updateSession = async () => {
        if (!editingSession) return;
        try {
            await api.put(`/admin/sessions/${editingSession.id}`, {
                title: editingSession.title,
                description: editingSession.description,
                scheduled_at: editingSession.scheduled_at,
                google_meet_link: editingSession.google_meet_link || ''
            });
            toast.success('Session updated!');
            setEditSessionModal(false);
            setEditingSession(null);
            loadData();
        } catch (err) {
            toast.error('Failed to update session');
        }
    };

    const deleteSession = async (sessionId) => {
        if (!confirm('Are you sure you want to cancel/delete this session?')) return;
        try {
            await api.delete(`/admin/sessions/${sessionId}`);
            toast.success('Session cancelled');
            loadData();
        } catch (err) {
            toast.error('Failed to delete session');
        }
    };

    const applyMeetLinkToAllFuture = async () => {
        if (!editingSession?.google_meet_link) {
            toast.error('Please enter a Google Meet link first');
            return;
        }
        if (!confirm(`Apply "${editingSession.google_meet_link}" to ALL future sessions? This will overwrite existing links on upcoming sessions.`)) return;
        try {
            const res = await api.put('/admin/sessions/bulk-meet-link', {
                google_meet_link: editingSession.google_meet_link
            });
            toast.success(`Applied to ${res.data.count} future session${res.data.count === 1 ? '' : 's'}!`);
            setEditSessionModal(false);
            setEditingSession(null);
            loadData();
        } catch (err) {
            toast.error('Failed to apply link to all sessions');
        }
    };

    const toggleSessionLive = async (sessionId) => {
        try {
            const res = await api.put(`/admin/sessions/${sessionId}/toggle-live`);
            toast.success(res.data.is_active ? 'Session is now LIVE!' : 'Session ended');
            loadData();
        } catch (err) {
            toast.error('Failed to toggle session status');
        }
    };

    const formatSessionDate = (dateStr) => {
        try {
            return new Date(dateStr).toLocaleString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch {
            return dateStr;
        }
    };

    // Course Video Management Functions
    const openEditVideo = (video) => {
        setEditingVideo({ ...video });
        setEditVideoModal(true);
    };

    const saveVideoEdit = async () => {
        try {
            await api.put(`/admin/course-videos/${editingVideo.id}`, {
                module: editingVideo.module,
                title: editingVideo.title,
                description: editingVideo.description,
                youtube_id: editingVideo.youtube_id,
                duration: editingVideo.duration
            });
            toast.success('Video updated');
            setEditVideoModal(false);
            setEditingVideo(null);
            loadData();
        } catch (err) {
            toast.error('Failed to update video');
        }
    };

    const deleteVideo = async (videoId) => {
        if (!confirm('Are you sure you want to delete this video?')) return;
        try {
            await api.delete(`/admin/course-videos/${videoId}`);
            toast.success('Video deleted');
            loadData();
        } catch (err) {
            toast.error('Failed to delete video');
        }
    };

    const addNewVideo = async () => {
        if (!newVideo.title || !newVideo.youtube_id || !newVideo.module) {
            toast.error('Please fill in all required fields');
            return;
        }
        try {
            await api.post('/admin/course-videos', newVideo);
            toast.success('Video added');
            setAddVideoModal(false);
            setNewVideo({ module: '', title: '', description: '', youtube_id: '', duration: '' });
            loadData();
        } catch (err) {
            toast.error('Failed to add video');
        }
    };

    // Get unique modules from videos
    const getUniqueModules = () => {
        const modules = [...new Set(courseVideos.map(v => v.module))];
        return modules.length > 0 ? modules : ['Getting Started', 'Application Process', 'Interview Preparation', 'Training & Beyond'];
    };

    // Service Management Functions
    const openEditService = (service, type) => {
        setEditingService({ ...service });
        setEditingServiceType(type);
        setEditServiceModal(true);
    };

    const saveServiceEdit = async () => {
        try {
            await api.put(`/admin/services/${editingServiceType}/${editingService.id}`, editingService);
            toast.success('Service updated');
            setEditServiceModal(false);
            setEditingService(null);
            setEditingServiceType(null);
            loadData();
        } catch (err) {
            toast.error('Failed to update service');
        }
    };

    const openAddService = (type) => {
        setAddServiceType(type);
        setNewService({ name: '', description: '', price: '', duration: '', from_scratch_price: '', expedited_fee: '50' });
        setAddServiceModal(true);
    };

    const addNewService = async () => {
        if (!newService.name || !newService.price) {
            toast.error('Name and price are required');
            return;
        }
        try {
            const payload = {
                name: newService.name,
                description: newService.description,
                price: parseFloat(newService.price)
            };
            if (addServiceType === 'coaching' && newService.duration) {
                payload.duration = parseInt(newService.duration);
            }
            if (addServiceType === 'resume') {
                if (newService.from_scratch_price) {
                    payload.from_scratch_price = parseFloat(newService.from_scratch_price);
                }
                payload.expedited_fee = parseFloat(newService.expedited_fee) || 50;
            }
            await api.post(`/admin/services/${addServiceType}`, payload);
            toast.success('Service added');
            setAddServiceModal(false);
            setNewService({ name: '', description: '', price: '', duration: '', from_scratch_price: '', expedited_fee: '50' });
            loadData();
        } catch (err) {
            toast.error('Failed to add service');
        }
    };

    const deleteService = async (type, serviceId) => {
        if (!confirm('Are you sure you want to delete this service?')) return;
        try {
            await api.delete(`/admin/services/${type}/${serviceId}`);
            toast.success('Service deleted');
            loadData();
        } catch (err) {
            toast.error('Failed to delete service');
        }
    };

    // Product Management Functions
    const openEditProduct = (productKey, product) => {
        setEditingProductKey(productKey);
        setEditingProduct({ ...product });
        setEditProductModal(true);
    };

    const saveProductEdit = async () => {
        try {
            await api.put(`/admin/products/${editingProductKey}`, {
                price: editingProduct.price,
                title: editingProduct.title,
                description: editingProduct.description
            });
            toast.success('Product updated');
            setEditProductModal(false);
            setEditingProduct(null);
            setEditingProductKey(null);
            loadData();
        } catch (err) {
            toast.error('Failed to update product');
        }
    };

    // Product categories for display
    const productCategories = {
        main: {
            title: 'Main Products',
            items: ['book', 'course', 'mentorship']
        },
        memberships: {
            title: 'Memberships',
            items: ['membership_basic', 'membership_premium', 'membership_vip']
        },
        coaching: {
            title: 'Coaching Sessions',
            items: ['coaching_phone', 'coaching_zoom']
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted/30">
            {/* Header */}
            <section className="py-8 bg-white border-b">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                                <LayoutDashboard className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <h1 className="font-heading text-2xl font-bold">Admin Dashboard</h1>
                                <p className="text-sm text-muted-foreground">Manage your Flight Attendant Academy</p>
                            </div>
                        </div>
                        <Button 
                            variant="outline" 
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="gap-2"
                        >
                            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                    </div>
                </div>
            </section>

            {/* Stats Overview */}
            <section className="py-8">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <Users className="h-8 w-8 text-blue-500" />
                                <div>
                                    <p className="text-2xl font-bold">{stats?.users?.total || 0}</p>
                                    <p className="text-xs text-muted-foreground">Total Users</p>
                                </div>
                            </div>
                        </Card>
                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <Crown className="h-8 w-8 text-yellow-500" />
                                <div>
                                    <p className="text-2xl font-bold">{stats?.memberships?.total || 0}</p>
                                    <p className="text-xs text-muted-foreground">Active Members</p>
                                </div>
                            </div>
                        </Card>
                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <Calendar className="h-8 w-8 text-green-500" />
                                <div>
                                    <p className="text-2xl font-bold">{stats?.coaching?.total_bookings || 0}</p>
                                    <p className="text-xs text-muted-foreground">Total Bookings</p>
                                </div>
                            </div>
                        </Card>
                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <CreditCard className="h-8 w-8 text-purple-500" />
                                <div>
                                    <p className="text-2xl font-bold">{stats?.transactions?.successful || 0}</p>
                                    <p className="text-xs text-muted-foreground">Payments</p>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Detailed Stats */}
                    <div className="grid md:grid-cols-3 gap-4 mb-8">
                        <Card className="p-4">
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <BookOpen className="h-4 w-4" />
                                Product Sales
                            </h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Book Purchases</span>
                                    <span className="font-medium">{stats?.users?.with_book || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Course Purchases</span>
                                    <span className="font-medium">{stats?.users?.with_course || 0}</span>
                                </div>
                            </div>
                        </Card>
                        <Card className="p-4">
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <Crown className="h-4 w-4" />
                                Membership Breakdown
                            </h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between items-center">
                                    <span>Basic ($29/mo)</span>
                                    <Badge variant="secondary">{stats?.memberships?.basic || 0}</Badge>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span>Premium ($49.99/mo)</span>
                                    <Badge className="bg-yellow-500">{stats?.memberships?.premium || 0}</Badge>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span>VIP ($99/mo)</span>
                                    <Badge className="bg-purple-600">{stats?.memberships?.vip || 0}</Badge>
                                </div>
                            </div>
                        </Card>
                        <Card className="p-4">
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                Coaching Stats
                            </h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between items-center">
                                    <span>Confirmed</span>
                                    <Badge className="bg-green-500">{stats?.coaching?.confirmed || 0}</Badge>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span>Pending</span>
                                    <Badge className="bg-yellow-500">{stats?.coaching?.pending || 0}</Badge>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span>VIP Free Sessions</span>
                                    <Badge className="bg-purple-600">{stats?.coaching?.vip_free_sessions || 0}</Badge>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Quick Actions */}
                    <Card className="p-6 mb-8 border-2 border-primary/20 bg-primary/5">
                        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                            <PlayCircle className="h-5 w-5 text-primary" />
                            Quick Actions
                        </h3>
                        <div className="grid md:grid-cols-4 gap-4">
                            <Button 
                                variant="outline"
                                className="h-auto py-4 flex flex-col items-center gap-2"
                                onClick={() => navigate('/book')}
                                data-testid="quick-action-book"
                            >
                                <BookOpen className="h-6 w-6" />
                                <span>View Book</span>
                                <span className="text-xs opacity-80">Book page</span>
                            </Button>
                            <Button 
                                variant="outline"
                                className="h-auto py-4 flex flex-col items-center gap-2"
                                onClick={() => navigate('/coaching')}
                            >
                                <Phone className="h-6 w-6" />
                                <span>View Coaching</span>
                                <span className="text-xs opacity-80">Manage sessions</span>
                            </Button>
                            <Button 
                                variant="outline"
                                className="h-auto py-4 flex flex-col items-center gap-2"
                                onClick={() => setAddAirlineModal(true)}
                            >
                                <Plane className="h-6 w-6" />
                                <span>Add Airline</span>
                                <span className="text-xs opacity-80">Update database</span>
                            </Button>
                            <Button 
                                variant="outline"
                                className="h-auto py-4 flex flex-col items-center gap-2"
                                onClick={() => navigate('/course')}
                            >
                                <GraduationCap className="h-6 w-6" />
                                <span>View Course</span>
                                <span className="text-xs opacity-80">Course content</span>
                            </Button>
                        </div>
                    </Card>

                    {/* Tabs for detailed management */}
                    <Tabs defaultValue="sessions" className="w-full">
                        <TabsList className="grid w-full grid-cols-8 mb-6">
                            <TabsTrigger value="sessions" className="gap-2">
                                <Video className="h-4 w-4" />
                                Live Sessions
                            </TabsTrigger>
                            <TabsTrigger value="videos" className="gap-2">
                                <PlayCircle className="h-4 w-4" />
                                Course Videos
                            </TabsTrigger>
                            <TabsTrigger value="products" className="gap-2">
                                <DollarSign className="h-4 w-4" />
                                Products
                            </TabsTrigger>
                            <TabsTrigger value="services" className="gap-2">
                                <Phone className="h-4 w-4" />
                                Services
                            </TabsTrigger>
                            <TabsTrigger value="bookings" className="gap-2">
                                <Calendar className="h-4 w-4" />
                                Bookings
                            </TabsTrigger>
                            <TabsTrigger value="users" className="gap-2">
                                <Users className="h-4 w-4" />
                                Users
                            </TabsTrigger>
                            <TabsTrigger value="transactions" className="gap-2">
                                <CreditCard className="h-4 w-4" />
                                Transactions
                            </TabsTrigger>
                            <TabsTrigger value="airlines" className="gap-2">
                                <Plane className="h-4 w-4" />
                                Airlines
                            </TabsTrigger>
                        </TabsList>

                        {/* Live Sessions Tab */}
                        <TabsContent value="sessions">
                            <Card className="p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h2 className="font-heading text-xl font-semibold">Weekly Live Sessions</h2>
                                        <p className="text-sm text-muted-foreground">
                                            Edit dates, times, and Google Meet links for your live sessions
                                        </p>
                                    </div>
                                    <Button onClick={() => setAddSessionModal(true)} className="gap-2">
                                        <Plus className="h-4 w-4" />
                                        New Session
                                    </Button>
                                </div>

                                {sessions.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <Video className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No live sessions yet</p>
                                        <Button 
                                            className="mt-4" 
                                            onClick={() => setAddSessionModal(true)}
                                        >
                                            Create Your First Session
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                                        {sessions
                                            .slice()
                                            .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
                                            .map((session) => (
                                            <Card key={session.id} className={`p-4 border ${session.is_active ? 'border-red-500 bg-red-50 dark:bg-red-900/10' : 'border-muted'}`}>
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                            {session.is_active && (
                                                                <Badge className="bg-red-500 animate-pulse">LIVE NOW</Badge>
                                                            )}
                                                            <h3 className="font-semibold">{session.title}</h3>
                                                        </div>
                                                        <div className="flex items-center gap-4 text-sm flex-wrap">
                                                            <span className="flex items-center gap-1">
                                                                <Calendar className="h-4 w-4" />
                                                                {formatSessionDate(session.scheduled_at)}
                                                            </span>
                                                            {session.google_meet_link ? (
                                                                <a 
                                                                    href={session.google_meet_link} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="flex items-center gap-1 text-green-600 hover:underline"
                                                                    title={session.google_meet_link}
                                                                >
                                                                    <Video className="h-4 w-4" />
                                                                    Meet link set
                                                                </a>
                                                            ) : (
                                                                <span className="flex items-center gap-1 text-amber-600">
                                                                    <Video className="h-4 w-4" />
                                                                    No Meet link
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => {
                                                                setEditingSession({...session});
                                                                setEditSessionModal(true);
                                                            }}
                                                            data-testid={`edit-session-${session.id}`}
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="text-red-500 hover:text-red-600"
                                                            onClick={() => deleteSession(session.id)}
                                                            data-testid={`delete-session-${session.id}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </TabsContent>

                        {/* Course Videos Tab */}
                        <TabsContent value="videos">
                            <Card className="p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h2 className="font-heading text-xl font-semibold">Course Videos</h2>
                                        <p className="text-sm text-muted-foreground">
                                            Add, edit, and remove video course links ({courseVideos.length} videos)
                                        </p>
                                    </div>
                                    <Button onClick={() => setAddVideoModal(true)} className="gap-2">
                                        <Plus className="h-4 w-4" />
                                        Add Video
                                    </Button>
                                </div>

                                {courseVideos.length === 0 ? (
                                    <p className="text-muted-foreground text-center py-8">No videos yet. Add your first video.</p>
                                ) : (
                                    <div className="space-y-4">
                                        {courseVideos.map((video) => (
                                            <Card key={video.id} className="p-4 flex items-center gap-4">
                                                {/* Thumbnail */}
                                                <div className="flex-shrink-0">
                                                    <img 
                                                        src={`https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`}
                                                        alt={video.title}
                                                        className="w-32 h-20 object-cover rounded-lg"
                                                    />
                                                </div>
                                                
                                                {/* Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Badge variant="outline" className="text-xs">{video.module}</Badge>
                                                        <span className="text-xs text-muted-foreground">{video.duration}</span>
                                                    </div>
                                                    <h4 className="font-medium truncate">{video.title}</h4>
                                                    <p className="text-sm text-muted-foreground truncate">{video.description}</p>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        YouTube ID: <code className="bg-muted px-1 rounded">{video.youtube_id}</code>
                                                    </p>
                                                </div>

                                                {/* Actions */}
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        onClick={() => window.open(`https://youtube.com/watch?v=${video.youtube_id}`, '_blank')}
                                                    >
                                                        <ExternalLink className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        onClick={() => openEditVideo(video)}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        className="text-red-500 hover:text-red-600"
                                                        onClick={() => deleteVideo(video.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </TabsContent>

                        {/* Products Tab */}
                        <TabsContent value="products">
                            <div className="space-y-6">
                                {/* Main Products: Book, Course, Mentorship */}
                                <Card className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                                                <BookOpen className="h-5 w-5 text-primary" />
                                                Main Products
                                            </h2>
                                            <p className="text-sm text-muted-foreground">Book, Course & Mentorship pricing</p>
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-3 gap-4">
                                        {['book', 'course', 'mentorship'].map((key) => {
                                            const product = products[key];
                                            if (!product) return null;
                                            return (
                                                <div key={key} className="p-4 border rounded-lg">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="font-medium">{product.title}</h4>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => openEditProduct(key, product)}
                                                            data-testid={`edit-product-${key}`}
                                                            className="gap-1"
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                            Edit
                                                        </Button>
                                                    </div>
                                                    <p className="text-2xl font-bold text-primary">${product.price}</p>
                                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card>

                                {/* Memberships */}
                                <Card className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                                                <Users className="h-5 w-5 text-primary" />
                                                Membership Tiers
                                            </h2>
                                            <p className="text-sm text-muted-foreground">Monthly subscription pricing</p>
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-3 gap-4">
                                        {['membership_basic', 'membership_premium', 'membership_vip'].map((key) => {
                                            const product = products[key];
                                            if (!product) return null;
                                            return (
                                                <div key={key} className="p-4 border rounded-lg">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="font-medium">{product.title}</h4>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => openEditProduct(key, product)}
                                                            data-testid={`edit-product-${key}`}
                                                            className="gap-1"
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                            Edit
                                                        </Button>
                                                    </div>
                                                    <p className="text-2xl font-bold text-primary">${product.price}<span className="text-sm text-muted-foreground">/mo</span></p>
                                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card>

                                {/* Coaching Packages */}
                                <Card className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                                                <Phone className="h-5 w-5 text-primary" />
                                                Coaching Packages
                                            </h2>
                                            <p className="text-sm text-muted-foreground">One-on-one coaching pricing</p>
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {['coaching_phone', 'coaching_zoom'].map((key) => {
                                            const product = products[key];
                                            if (!product) return null;
                                            return (
                                                <div key={key} className="p-4 border rounded-lg">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="font-medium">{product.title}</h4>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => openEditProduct(key, product)}
                                                            data-testid={`edit-product-${key}`}
                                                            className="gap-1"
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                            Edit
                                                        </Button>
                                                    </div>
                                                    <p className="text-2xl font-bold text-primary">${product.price}</p>
                                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card>
                            </div>
                        </TabsContent>

                        {/* Services Tab */}
                        <TabsContent value="services">
                            {/* Preview Banner */}
                            <Card className="p-4 mb-6 border-primary/30 bg-primary/5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Eye className="h-5 w-5 text-primary" />
                                        <div>
                                            <p className="font-medium">Preview Changes</p>
                                            <p className="text-sm text-muted-foreground">See how your services will appear on the Coaching page</p>
                                        </div>
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        onClick={() => setShowPreviewModal(true)}
                                        className="gap-2"
                                        data-testid="preview-services-btn"
                                    >
                                        <Eye className="h-4 w-4" />
                                        Preview Services
                                    </Button>
                                </div>
                            </Card>
                            
                            <div className="grid md:grid-cols-2 gap-6">
                                {/* Coaching Services */}
                                <Card className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                                                <Phone className="h-5 w-5 text-primary" />
                                                Coaching Sessions
                                            </h2>
                                            <p className="text-sm text-muted-foreground">Manage coaching session pricing</p>
                                        </div>
                                        <Button size="sm" onClick={() => openAddService('coaching')} className="gap-1">
                                            <Plus className="h-4 w-4" />
                                            Add Session
                                        </Button>
                                    </div>
                                    <div className="space-y-3">
                                        {services.coaching?.map((service) => (
                                            <div key={service.id} className="flex items-center justify-between p-3 border rounded-lg">
                                                <div>
                                                    <h4 className="font-medium">{service.name}</h4>
                                                    <p className="text-sm text-muted-foreground">{service.duration} minutes</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg font-bold text-primary">${service.price}</span>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        onClick={() => openEditService(service, 'coaching')}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        className="text-red-500 hover:text-red-600"
                                                        onClick={() => deleteService('coaching', service.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                        {(!services.coaching || services.coaching.length === 0) && (
                                            <p className="text-center text-muted-foreground py-4">No coaching sessions. Add one to get started.</p>
                                        )}
                                    </div>
                                </Card>

                                {/* Resume Services */}
                                <Card className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                                                <BookOpen className="h-5 w-5 text-primary" />
                                                Resume Services
                                            </h2>
                                            <p className="text-sm text-muted-foreground">Manage resume service pricing</p>
                                        </div>
                                        <Button size="sm" onClick={() => openAddService('resume')} className="gap-1">
                                            <Plus className="h-4 w-4" />
                                            Add Service
                                        </Button>
                                    </div>
                                    <div className="space-y-3">
                                        {services.resume?.map((service) => (
                                            <div key={service.id} className="flex items-center justify-between p-3 border rounded-lg">
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-medium">{service.name}</h4>
                                                    <p className="text-sm text-muted-foreground truncate">{service.description}</p>
                                                    {service.from_scratch_price && (
                                                        <p className="text-xs text-blue-600">From scratch: ${service.from_scratch_price}</p>
                                                    )}
                                                    <p className="text-xs text-amber-600">Expedited: +${service.expedited_fee}</p>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <span className="text-lg font-bold text-primary">${service.price}</span>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        onClick={() => openEditService(service, 'resume')}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        className="text-red-500 hover:text-red-600"
                                                        onClick={() => deleteService('resume', service.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                        {(!services.resume || services.resume.length === 0) && (
                                            <p className="text-center text-muted-foreground py-4">No resume services. Add one to get started.</p>
                                        )}
                                    </div>
                                </Card>
                            </div>
                        </TabsContent>

                        {/* Bookings Tab */}
                        <TabsContent value="bookings">
                            <Card className="p-6">
                                <h2 className="font-heading text-xl font-semibold mb-4">Coaching Bookings</h2>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left p-2">Date/Time</th>
                                                <th className="text-left p-2">Client</th>
                                                <th className="text-left p-2">Type</th>
                                                <th className="text-left p-2">Status</th>
                                                <th className="text-left p-2">Payment</th>
                                                <th className="text-left p-2">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bookings.map((booking) => (
                                                <tr key={booking.id} className="border-b hover:bg-muted/50">
                                                    <td className="p-2">
                                                        <div>{booking.booking_date}</div>
                                                        <div className="text-xs text-muted-foreground">{booking.booking_time} MST</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div>{booking.user_name || 'N/A'}</div>
                                                        <div className="text-xs text-muted-foreground">{booking.user_email}</div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="flex items-center gap-1">
                                                            {booking.session_type === 'vip_free_coaching' ? (
                                                                <><Gift className="h-4 w-4 text-purple-600" /> VIP Free</>
                                                            ) : booking.session_type === 'coaching_phone' ? (
                                                                <><Phone className="h-4 w-4" /> Phone</>
                                                            ) : (
                                                                <><Video className="h-4 w-4" /> Video</>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-2">
                                                        <Badge className={
                                                            booking.status === 'confirmed' ? 'bg-green-500' :
                                                            booking.status === 'completed' ? 'bg-blue-500' :
                                                            booking.status === 'cancelled' ? 'bg-red-500' :
                                                            'bg-yellow-500'
                                                        }>
                                                            {booking.status}
                                                        </Badge>
                                                    </td>
                                                    <td className="p-2">
                                                        <Badge variant={booking.payment_status === 'paid' || booking.payment_status === 'free' ? 'default' : 'secondary'}>
                                                            {booking.payment_status}
                                                        </Badge>
                                                    </td>
                                                    <td className="p-2">
                                                        <Select
                                                            value={booking.status}
                                                            onValueChange={(value) => updateBookingStatus(booking.id, value)}
                                                        >
                                                            <SelectTrigger className="w-[120px] h-8">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="pending">Pending</SelectItem>
                                                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                                                <SelectItem value="completed">Completed</SelectItem>
                                                                <SelectItem value="cancelled">Cancelled</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {bookings.length === 0 && (
                                        <div className="text-center py-8 text-muted-foreground">
                                            No bookings yet
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </TabsContent>

                        {/* Users Tab */}
                        <TabsContent value="users">
                            <Card className="p-6">
                                <h2 className="font-heading text-xl font-semibold mb-4">Users ({users.length})</h2>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left p-2">Name</th>
                                                <th className="text-left p-2">Email</th>
                                                <th className="text-left p-2">Products</th>
                                                <th className="text-left p-2">Membership</th>
                                                <th className="text-left p-2">Joined</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((u) => (
                                                <tr key={u.id} className="border-b hover:bg-muted/50">
                                                    <td className="p-2">{u.name || 'N/A'}</td>
                                                    <td className="p-2">{u.email}</td>
                                                    <td className="p-2">
                                                        <div className="flex gap-1">
                                                            {u.has_book_access && <Badge variant="outline" className="text-xs">Book</Badge>}
                                                            {u.has_course_access && <Badge variant="outline" className="text-xs">Course</Badge>}
                                                        </div>
                                                    </td>
                                                    <td className="p-2">
                                                        {u.membership_type === 'membership_vip' ? (
                                                            <Badge className="bg-purple-600"><Sparkles className="h-3 w-3 mr-1" />VIP</Badge>
                                                        ) : u.membership_type === 'membership_premium' ? (
                                                            <Badge className="bg-yellow-500"><Crown className="h-3 w-3 mr-1" />Premium</Badge>
                                                        ) : u.membership_type === 'membership_basic' ? (
                                                            <Badge className="bg-green-500">Basic</Badge>
                                                        ) : (
                                                            <span className="text-muted-foreground">None</span>
                                                        )}
                                                    </td>
                                                    <td className="p-2 text-muted-foreground">
                                                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {users.length === 0 && (
                                        <div className="text-center py-8 text-muted-foreground">
                                            No users yet
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </TabsContent>

                        {/* Transactions Tab */}
                        <TabsContent value="transactions">
                            <Card className="p-6">
                                <h2 className="font-heading text-xl font-semibold mb-4">Recent Transactions</h2>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left p-2">Date</th>
                                                <th className="text-left p-2">User</th>
                                                <th className="text-left p-2">Product</th>
                                                <th className="text-left p-2">Amount</th>
                                                <th className="text-left p-2">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.map((t) => (
                                                <tr key={t.id} className="border-b hover:bg-muted/50">
                                                    <td className="p-2">
                                                        {t.created_at ? new Date(t.created_at).toLocaleDateString() : 'N/A'}
                                                    </td>
                                                    <td className="p-2">{t.user_email}</td>
                                                    <td className="p-2">
                                                        <Badge variant="outline">{t.product_type}</Badge>
                                                    </td>
                                                    <td className="p-2 font-medium">${t.amount}</td>
                                                    <td className="p-2">
                                                        <Badge className={t.payment_status === 'paid' ? 'bg-green-500' : 'bg-yellow-500'}>
                                                            {t.payment_status}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {transactions.length === 0 && (
                                        <div className="text-center py-8 text-muted-foreground">
                                            No transactions yet
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </TabsContent>

                        {/* Airlines Tab */}
                        <TabsContent value="airlines">
                            <Card className="p-6">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                                    <div>
                                        <h2 className="font-heading text-xl font-semibold">Airlines Database</h2>
                                        <p className="text-sm text-muted-foreground">
                                            {getAirlineStats().total} total airlines • {getAirlineStats().hiring} currently hiring
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="Search airlines..."
                                            value={airlineSearch}
                                            onChange={(e) => setAirlineSearch(e.target.value)}
                                            className="w-48"
                                        />
                                        <Button onClick={() => setAddAirlineModal(true)} className="gap-2">
                                            <Plus className="h-4 w-4" />
                                            Add Airline
                                        </Button>
                                    </div>
                                </div>

                                {/* Quick Stats */}
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                                    {Object.entries(airlines).map(([category, data]) => (
                                        <div key={category} className="bg-muted/50 rounded-lg p-3 text-center">
                                            <p className="text-2xl font-bold text-primary">{data.airlines?.length || 0}</p>
                                            <p className="text-xs text-muted-foreground">{data.title}</p>
                                            <p className="text-xs text-green-600">
                                                {data.airlines?.filter(a => a.accepting).length || 0} hiring
                                            </p>
                                        </div>
                                    ))}
                                </div>
                                
                                {Object.entries(airlines).map(([category, data]) => {
                                    const filteredAirlines = filterAirlines(data.airlines || []);
                                    if (filteredAirlines.length === 0 && airlineSearch) return null;
                                    
                                    return (
                                        <div key={category} className="mb-6">
                                            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                                                {data.title}
                                                <Badge variant="outline" className="text-xs">
                                                    {filteredAirlines.length} airlines
                                                </Badge>
                                            </h3>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-sm">
                                                    <thead>
                                                        <tr className="border-b bg-muted/30">
                                                            <th className="text-left p-3">Airline</th>
                                                            <th className="text-left p-3">Hiring Status</th>
                                                            <th className="text-left p-3">Note</th>
                                                            <th className="text-left p-3">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredAirlines.map((airline) => (
                                                            <tr key={airline.name} className="border-b hover:bg-muted/50">
                                                                <td className="p-3">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-medium">{airline.name}</span>
                                                                        <a href={airline.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary">
                                                                            <ExternalLink className="h-3 w-3" />
                                                                        </a>
                                                                    </div>
                                                                </td>
                                                                <td className="p-3">
                                                                    <Button
                                                                        size="sm"
                                                                        variant={airline.accepting ? "default" : "outline"}
                                                                        className={`gap-1 ${airline.accepting ? 'bg-green-500 hover:bg-green-600' : ''}`}
                                                                        onClick={() => toggleHiringStatus(category, airline)}
                                                                    >
                                                                        {airline.accepting ? (
                                                                            <>
                                                                                <Check className="h-3 w-3" />
                                                                                Hiring
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <X className="h-3 w-3" />
                                                                                Not Hiring
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                </td>
                                                                <td className="p-3 text-muted-foreground">
                                                                    {airline.note || '-'}
                                                                </td>
                                                                <td className="p-3">
                                                                    <div className="flex gap-1">
                                                                        <Button 
                                                                            size="sm" 
                                                                            variant="ghost"
                                                                            onClick={() => openEditAirline(category, airline)}
                                                                            title="Edit airline"
                                                                        >
                                                                            <Edit className="h-4 w-4" />
                                                                        </Button>
                                                                        <Button 
                                                                            size="sm" 
                                                                            variant="ghost"
                                                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                                            onClick={() => deleteAirline(category, airline.name)}
                                                                            title="Delete airline"
                                                                        >
                                                                            <Trash2 className="h-4 w-4" />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })}
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>
            </section>

            {/* Edit Airline Modal */}
            <Dialog open={editAirlineModal} onOpenChange={setEditAirlineModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Airline</DialogTitle>
                        <DialogDescription>Update airline information</DialogDescription>
                    </DialogHeader>
                    {editingAirline && (
                        <div className="space-y-4">
                            <div>
                                <Label>Airline Name</Label>
                                <Input value={editingAirline.name} disabled className="mt-1" />
                            </div>
                            <div>
                                <Label>URL</Label>
                                <Input 
                                    value={editingAirline.url} 
                                    onChange={(e) => setEditingAirline({...editingAirline, url: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={editingAirline.accepting}
                                    onChange={(e) => setEditingAirline({...editingAirline, accepting: e.target.checked})}
                                    id="accepting"
                                />
                                <Label htmlFor="accepting">Currently Hiring</Label>
                            </div>
                            <div>
                                <Label>Note (optional)</Label>
                                <Input 
                                    value={editingAirline.note || ''} 
                                    onChange={(e) => setEditingAirline({...editingAirline, note: e.target.value})}
                                    placeholder="e.g., Base cities"
                                    className="mt-1" 
                                />
                            </div>
                            <Button onClick={saveAirlineEdit} className="w-full">Save Changes</Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Add Airline Modal */}
            <Dialog open={addAirlineModal} onOpenChange={setAddAirlineModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add New Airline</DialogTitle>
                        <DialogDescription>Add a new airline to the database</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Category</Label>
                            <Select value={addCategory} onValueChange={setAddCategory}>
                                <SelectTrigger className="mt-1">
                                    <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(airlines).map(([key, data]) => (
                                        <SelectItem key={key} value={key}>{data.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Airline Name *</Label>
                            <Input 
                                value={newAirline.name} 
                                onChange={(e) => setNewAirline({...newAirline, name: e.target.value})}
                                placeholder="e.g., Delta Airlines"
                                className="mt-1" 
                            />
                        </div>
                        <div>
                            <Label>Careers URL *</Label>
                            <Input 
                                value={newAirline.url} 
                                onChange={(e) => setNewAirline({...newAirline, url: e.target.value})}
                                placeholder="https://..."
                                className="mt-1" 
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={newAirline.accepting}
                                onChange={(e) => setNewAirline({...newAirline, accepting: e.target.checked})}
                                id="newAccepting"
                            />
                            <Label htmlFor="newAccepting">Currently Hiring</Label>
                        </div>
                        <div>
                            <Label>Note (optional)</Label>
                            <Input 
                                value={newAirline.note} 
                                onChange={(e) => setNewAirline({...newAirline, note: e.target.value})}
                                placeholder="e.g., Base cities"
                                className="mt-1" 
                            />
                        </div>
                        <Button onClick={addNewAirline} className="w-full">Add Airline</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Session Modal */}
            <Dialog open={addSessionModal} onOpenChange={setAddSessionModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Video className="h-5 w-5 text-primary" />
                            Create New Live Session
                        </DialogTitle>
                        <DialogDescription>
                            Create a new live video session for your members
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Session Title *</Label>
                            <Input 
                                value={newSession.title} 
                                onChange={(e) => setNewSession({...newSession, title: e.target.value})}
                                placeholder="e.g., Weekly Q&A Session"
                                className="mt-1" 
                            />
                        </div>
                        <div>
                            <Label>Description (optional)</Label>
                            <Input 
                                value={newSession.description} 
                                onChange={(e) => setNewSession({...newSession, description: e.target.value})}
                                placeholder="e.g., Join us to discuss interview tips"
                                className="mt-1" 
                            />
                        </div>
                        <div>
                            <Label>Date & Time *</Label>
                            <Input 
                                type="datetime-local"
                                value={newSession.scheduled_at} 
                                onChange={(e) => setNewSession({...newSession, scheduled_at: e.target.value})}
                                className="mt-1" 
                            />
                        </div>
                        <div>
                            <Label className="flex items-center gap-2">
                                <Video className="h-4 w-4 text-primary" />
                                Google Meet Link
                            </Label>
                            <Input 
                                value={newSession.google_meet_link || ''} 
                                onChange={(e) => setNewSession({...newSession, google_meet_link: e.target.value})}
                                placeholder="https://meet.google.com/xxx-xxxx-xxx"
                                className="mt-1" 
                                data-testid="new-session-google-meet-link"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Paid members only. Create a Google Meet and paste the link here.
                            </p>
                        </div>
                        <div className="rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 p-4">
                            <div className="flex items-center justify-between mb-2">
                                <Label className="flex items-center gap-2 mb-0">
                                    <Calendar className="h-4 w-4 text-primary" />
                                    Auto-repeat Weekly
                                </Label>
                                <select
                                    value={newSession.weeks_count || 1}
                                    onChange={(e) => setNewSession({...newSession, weeks_count: parseInt(e.target.value, 10)})}
                                    className="text-sm rounded-md border border-input bg-background px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary"
                                    data-testid="new-session-weeks-count"
                                >
                                    <option value={1}>Just this one</option>
                                    <option value={4}>4 weeks (1 month)</option>
                                    <option value={8}>8 weeks (2 months)</option>
                                    <option value={12}>12 weeks (3 months)</option>
                                    <option value={24}>24 weeks (6 months)</option>
                                    <option value={52}>52 weeks (1 year)</option>
                                </select>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {(newSession.weeks_count || 1) > 1
                                    ? `Will create ${newSession.weeks_count} sessions, one per week starting on the date above.`
                                    : 'Select more than 1 to create a recurring series.'}
                            </p>
                        </div>
                        <Button onClick={createSession} className="w-full gap-2">
                            <Plus className="h-4 w-4" />
                            {(newSession.weeks_count || 1) > 1 ? `Create ${newSession.weeks_count} Weekly Sessions` : 'Create Session'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Session Modal */}
            <Dialog open={editSessionModal} onOpenChange={setEditSessionModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="h-5 w-5 text-primary" />
                            Edit Live Session
                        </DialogTitle>
                        <DialogDescription>
                            Update session details or change the schedule
                        </DialogDescription>
                    </DialogHeader>
                    {editingSession && (
                        <div className="space-y-4">
                            <div>
                                <Label>Session Title *</Label>
                                <Input 
                                    value={editingSession.title} 
                                    onChange={(e) => setEditingSession({...editingSession, title: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Input 
                                    value={editingSession.description || ''} 
                                    onChange={(e) => setEditingSession({...editingSession, description: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div>
                                <Label>Date & Time *</Label>
                                <Input 
                                    type="datetime-local"
                                    value={editingSession.scheduled_at?.slice(0, 16) || ''} 
                                    onChange={(e) => setEditingSession({...editingSession, scheduled_at: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div>
                                <Label className="flex items-center gap-2">
                                    <Video className="h-4 w-4 text-primary" />
                                    Google Meet Link
                                </Label>
                                <Input 
                                    value={editingSession.google_meet_link || ''} 
                                    onChange={(e) => setEditingSession({...editingSession, google_meet_link: e.target.value})}
                                    placeholder="https://meet.google.com/xxx-xxxx-xxx"
                                    className="mt-1" 
                                    data-testid="edit-session-google-meet-link"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Members see this link when they click "Join Session".
                                </p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={applyMeetLinkToAllFuture}
                                    className="mt-2 w-full gap-2 border-primary/40 text-primary hover:bg-primary/5"
                                    data-testid="apply-meet-link-all-btn"
                                >
                                    <Video className="h-3.5 w-3.5" />
                                    Apply this link to ALL future sessions
                                </Button>
                            </div>
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline" 
                                    onClick={() => setEditSessionModal(false)}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button onClick={updateSession} className="flex-1 gap-2">
                                    <Check className="h-4 w-4" />
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Add Video Modal */}
            <Dialog open={addVideoModal} onOpenChange={setAddVideoModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <PlayCircle className="h-5 w-5 text-primary" />
                            Add Course Video
                        </DialogTitle>
                        <DialogDescription>
                            Add a new YouTube video to your course library
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Module *</Label>
                            <Select 
                                value={newVideo.module} 
                                onValueChange={(value) => setNewVideo({...newVideo, module: value})}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue placeholder="Select module" />
                                </SelectTrigger>
                                <SelectContent>
                                    {getUniqueModules().map((mod) => (
                                        <SelectItem key={mod} value={mod}>{mod}</SelectItem>
                                    ))}
                                    <SelectItem value="new">+ New Module</SelectItem>
                                </SelectContent>
                            </Select>
                            {newVideo.module === 'new' && (
                                <Input 
                                    value={newVideo.customModule || ''} 
                                    onChange={(e) => setNewVideo({...newVideo, module: e.target.value, customModule: e.target.value})}
                                    placeholder="Enter new module name"
                                    className="mt-2" 
                                />
                            )}
                        </div>
                        <div>
                            <Label>Video Title *</Label>
                            <Input 
                                value={newVideo.title} 
                                onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
                                placeholder="e.g., How to Ace Your Interview"
                                className="mt-1" 
                            />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Input 
                                value={newVideo.description} 
                                onChange={(e) => setNewVideo({...newVideo, description: e.target.value})}
                                placeholder="e.g., Learn the secrets to interview success"
                                className="mt-1" 
                            />
                        </div>
                        <div>
                            <Label>YouTube Video ID *</Label>
                            <Input 
                                value={newVideo.youtube_id} 
                                onChange={(e) => setNewVideo({...newVideo, youtube_id: e.target.value})}
                                placeholder="e.g., tGp3BNM-vRI"
                                className="mt-1" 
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Find this in the YouTube URL: youtube.com/watch?v=<strong>tGp3BNM-vRI</strong>
                            </p>
                        </div>
                        <div>
                            <Label>Duration</Label>
                            <Input 
                                value={newVideo.duration} 
                                onChange={(e) => setNewVideo({...newVideo, duration: e.target.value})}
                                placeholder="e.g., 10 min"
                                className="mt-1" 
                            />
                        </div>
                        <Button onClick={addNewVideo} className="w-full">Add Video</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Video Modal */}
            <Dialog open={editVideoModal} onOpenChange={setEditVideoModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="h-5 w-5 text-primary" />
                            Edit Course Video
                        </DialogTitle>
                    </DialogHeader>
                    {editingVideo && (
                        <div className="space-y-4">
                            <div>
                                <Label>Module</Label>
                                <Select 
                                    value={editingVideo.module} 
                                    onValueChange={(value) => setEditingVideo({...editingVideo, module: value})}
                                >
                                    <SelectTrigger className="mt-1">
                                        <SelectValue placeholder="Select module" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {getUniqueModules().map((mod) => (
                                            <SelectItem key={mod} value={mod}>{mod}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Video Title</Label>
                                <Input 
                                    value={editingVideo.title} 
                                    onChange={(e) => setEditingVideo({...editingVideo, title: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Input 
                                    value={editingVideo.description} 
                                    onChange={(e) => setEditingVideo({...editingVideo, description: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div>
                                <Label>YouTube Video ID</Label>
                                <Input 
                                    value={editingVideo.youtube_id} 
                                    onChange={(e) => setEditingVideo({...editingVideo, youtube_id: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div>
                                <Label>Duration</Label>
                                <Input 
                                    value={editingVideo.duration} 
                                    onChange={(e) => setEditingVideo({...editingVideo, duration: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline" 
                                    onClick={() => setEditVideoModal(false)}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button onClick={saveVideoEdit} className="flex-1 gap-2">
                                    <Check className="h-4 w-4" />
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit Service Modal */}
            <Dialog open={editServiceModal} onOpenChange={setEditServiceModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="h-5 w-5 text-primary" />
                            Edit {editingServiceType === 'coaching' ? 'Coaching Session' : 'Resume Service'}
                        </DialogTitle>
                    </DialogHeader>
                    {editingService && (
                        <div className="space-y-4">
                            <div>
                                <Label>Name</Label>
                                <Input 
                                    value={editingService.name} 
                                    onChange={(e) => setEditingService({...editingService, name: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Input 
                                    value={editingService.description} 
                                    onChange={(e) => setEditingService({...editingService, description: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div>
                                <Label>Price ($)</Label>
                                <Input 
                                    type="number"
                                    value={editingService.price} 
                                    onChange={(e) => setEditingService({...editingService, price: parseFloat(e.target.value)})}
                                    className="mt-1" 
                                />
                            </div>
                            {editingServiceType === 'coaching' && (
                                <div>
                                    <Label>Duration (minutes)</Label>
                                    <Input 
                                        type="number"
                                        value={editingService.duration} 
                                        onChange={(e) => setEditingService({...editingService, duration: parseInt(e.target.value)})}
                                        className="mt-1" 
                                    />
                                </div>
                            )}
                            {editingServiceType === 'resume' && editingService.from_scratch_price !== undefined && (
                                <div>
                                    <Label>From Scratch Price ($)</Label>
                                    <Input 
                                        type="number"
                                        value={editingService.from_scratch_price} 
                                        onChange={(e) => setEditingService({...editingService, from_scratch_price: parseFloat(e.target.value)})}
                                        className="mt-1" 
                                    />
                                </div>
                            )}
                            {editingServiceType === 'resume' && (
                                <div>
                                    <Label>Expedited Fee ($)</Label>
                                    <Input 
                                        type="number"
                                        value={editingService.expedited_fee} 
                                        onChange={(e) => setEditingService({...editingService, expedited_fee: parseFloat(e.target.value)})}
                                        className="mt-1" 
                                    />
                                </div>
                            )}
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline" 
                                    onClick={() => setEditServiceModal(false)}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button onClick={saveServiceEdit} className="flex-1 gap-2">
                                    <Check className="h-4 w-4" />
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit Product Modal */}
            <Dialog open={editProductModal} onOpenChange={setEditProductModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <DollarSign className="h-5 w-5 text-primary" />
                            Edit Product Pricing
                        </DialogTitle>
                    </DialogHeader>
                    {editingProduct && (
                        <div className="space-y-4">
                            <div>
                                <Label>Product Name</Label>
                                <Input 
                                    value={editingProduct.title} 
                                    onChange={(e) => setEditingProduct({...editingProduct, title: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Input 
                                    value={editingProduct.description || ''} 
                                    onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                            <div>
                                <Label>Price ($)</Label>
                                <Input 
                                    type="number"
                                    step="0.01"
                                    value={editingProduct.price} 
                                    onChange={(e) => setEditingProduct({...editingProduct, price: parseFloat(e.target.value)})}
                                    className="mt-1" 
                                />
                                {editingProductKey?.includes('membership') && (
                                    <p className="text-xs text-muted-foreground mt-1">This is the monthly subscription price</p>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline" 
                                    onClick={() => setEditProductModal(false)}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button onClick={saveProductEdit} className="flex-1 gap-2">
                                    <Check className="h-4 w-4" />
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Add Service Modal */}
            <Dialog open={addServiceModal} onOpenChange={setAddServiceModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="h-5 w-5 text-primary" />
                            Add New {addServiceType === 'coaching' ? 'Coaching Session' : 'Resume Service'}
                        </DialogTitle>
                        <DialogDescription>
                            {addServiceType === 'coaching' 
                                ? 'Create a new coaching session package for customers to book'
                                : 'Add a new resume service offering'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Service Name *</Label>
                            <Input 
                                placeholder={addServiceType === 'coaching' ? 'e.g., 60 Minute Call' : 'e.g., Cover Letter'}
                                value={newService.name} 
                                onChange={(e) => setNewService({...newService, name: e.target.value})}
                                className="mt-1" 
                            />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Input 
                                placeholder={addServiceType === 'coaching' ? 'Full coaching session' : 'Professional cover letter creation'}
                                value={newService.description} 
                                onChange={(e) => setNewService({...newService, description: e.target.value})}
                                className="mt-1" 
                            />
                        </div>
                        <div>
                            <Label>Price ($) *</Label>
                            <Input 
                                type="number"
                                step="0.01"
                                placeholder="e.g., 150"
                                value={newService.price} 
                                onChange={(e) => setNewService({...newService, price: e.target.value})}
                                className="mt-1" 
                            />
                        </div>
                        {addServiceType === 'coaching' && (
                            <div>
                                <Label>Duration (minutes)</Label>
                                <Input 
                                    type="number"
                                    placeholder="e.g., 60"
                                    value={newService.duration} 
                                    onChange={(e) => setNewService({...newService, duration: e.target.value})}
                                    className="mt-1" 
                                />
                            </div>
                        )}
                        {addServiceType === 'resume' && (
                            <>
                                <div>
                                    <Label>From Scratch Price ($)</Label>
                                    <Input 
                                        type="number"
                                        step="0.01"
                                        placeholder="e.g., 175 (leave blank if not applicable)"
                                        value={newService.from_scratch_price} 
                                        onChange={(e) => setNewService({...newService, from_scratch_price: e.target.value})}
                                        className="mt-1" 
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">Price when customer doesn't have existing resume</p>
                                </div>
                                <div>
                                    <Label>Expedited Fee ($)</Label>
                                    <Input 
                                        type="number"
                                        step="0.01"
                                        placeholder="50"
                                        value={newService.expedited_fee} 
                                        onChange={(e) => setNewService({...newService, expedited_fee: e.target.value})}
                                        className="mt-1" 
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">Additional fee for same-day delivery</p>
                                </div>
                            </>
                        )}
                        <div className="flex gap-2 pt-2">
                            <Button 
                                variant="outline" 
                                onClick={() => setAddServiceModal(false)}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button onClick={addNewService} className="flex-1 gap-2">
                                <Plus className="h-4 w-4" />
                                Add Service
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Services Preview Modal */}
            <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Eye className="h-5 w-5 text-primary" />
                            Preview: How Services Appear on Coaching Page
                        </DialogTitle>
                        <DialogDescription>
                            This preview shows exactly how your resume services will appear to customers
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="mt-4">
                        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                            <FileText className="h-5 w-5 text-primary" />
                            Resume Services Preview
                        </h3>
                        
                        <div className="grid md:grid-cols-3 gap-4">
                            {/* New Resume Preview Card */}
                            {services.resume?.find(s => s.name === 'New Resume' || s.id === 'new_resume') && (
                                <div className="p-4 border rounded-lg hover:shadow-lg transition-shadow">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="h-10 w-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                                            <FileText className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">New Resume</h4>
                                            <p className="text-xs text-muted-foreground">Full resume creation</p>
                                        </div>
                                    </div>
                                    <div className="mb-2">
                                        <span className="text-2xl font-bold text-primary">
                                            ${services.resume.find(s => s.name === 'New Resume' || s.id === 'new_resume')?.price || 100}
                                        </span>
                                        <span className="text-sm text-muted-foreground ml-2">with PDF</span>
                                    </div>
                                    <div className="space-y-1 mb-3">
                                        <div className="bg-amber-50 border border-amber-200 rounded p-1.5">
                                            <p className="text-xs text-amber-700 flex items-center gap-1">
                                                <Zap className="h-3 w-3" />
                                                <strong>Expedited: +${services.resume.find(s => s.name === 'New Resume' || s.id === 'new_resume')?.expedited_fee || 50}</strong>
                                            </p>
                                        </div>
                                        <div className="bg-blue-50 border border-blue-200 rounded p-1.5">
                                            <p className="text-xs text-blue-700 flex items-center gap-1">
                                                <FileText className="h-3 w-3" />
                                                <strong>No PDF? ${services.resume.find(s => s.name === 'New Resume' || s.id === 'new_resume')?.from_scratch_price || 175}</strong>
                                            </p>
                                        </div>
                                    </div>
                                    <ul className="text-xs text-muted-foreground space-y-1 mb-3">
                                        <li className="flex items-center gap-1">
                                            <Check className="h-3 w-3 text-green-500" />
                                            1 airline per resume
                                        </li>
                                        <li className="flex items-center gap-1">
                                            <Check className="h-3 w-3 text-green-500" />
                                            Airline-specific keywords
                                        </li>
                                    </ul>
                                    <Button size="sm" className="w-full rounded-full" disabled>
                                        Order Now
                                    </Button>
                                </div>
                            )}
                            
                            {/* Added Airline Preview Card */}
                            {services.resume?.find(s => s.name === 'Added Airline' || s.id === 'added_airline') && (
                                <div className="p-4 border rounded-lg hover:shadow-lg transition-shadow">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                                            <FileText className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">Added Airline</h4>
                                            <p className="text-xs text-muted-foreground">Additional airline version</p>
                                        </div>
                                    </div>
                                    <div className="mb-2">
                                        <span className="text-2xl font-bold text-primary">
                                            ${services.resume.find(s => s.name === 'Added Airline' || s.id === 'added_airline')?.price || 75}
                                        </span>
                                    </div>
                                    <div className="bg-amber-50 border border-amber-200 rounded p-1.5 mb-3">
                                        <p className="text-xs text-amber-700 flex items-center gap-1">
                                            <Zap className="h-3 w-3" />
                                            <strong>Expedited: +${services.resume.find(s => s.name === 'Added Airline' || s.id === 'added_airline')?.expedited_fee || 50}</strong>
                                        </p>
                                    </div>
                                    <ul className="text-xs text-muted-foreground space-y-1 mb-3">
                                        <li className="flex items-center gap-1">
                                            <Check className="h-3 w-3 text-green-500" />
                                            Add to existing resume
                                        </li>
                                        <li className="flex items-center gap-1">
                                            <Check className="h-3 w-3 text-green-500" />
                                            Airline-specific skills
                                        </li>
                                    </ul>
                                    <Button size="sm" className="w-full rounded-full" disabled>
                                        Order Now
                                    </Button>
                                </div>
                            )}
                            
                            {/* Re-edit Resume Preview Card */}
                            {services.resume?.find(s => s.name === 'Re-edit Resume' || s.id === 'reedit_resume') && (
                                <div className="p-4 border rounded-lg hover:shadow-lg transition-shadow border-amber-200 bg-amber-50/30 relative">
                                    <Badge className="absolute -top-2 right-2 bg-amber-500 text-white text-xs">Returning Clients</Badge>
                                    <div className="flex items-center gap-2 mb-3 mt-1">
                                        <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                                            <RefreshCw className="h-5 w-5 text-amber-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">Re-edit Resume</h4>
                                            <p className="text-xs text-muted-foreground">Updates & revisions</p>
                                        </div>
                                    </div>
                                    <div className="mb-2">
                                        <span className="text-2xl font-bold text-primary">
                                            ${services.resume.find(s => s.name === 'Re-edit Resume' || s.id === 'reedit_resume')?.price || 50}
                                        </span>
                                    </div>
                                    <div className="bg-amber-50 border border-amber-200 rounded p-1.5 mb-3">
                                        <p className="text-xs text-amber-700 flex items-center gap-1">
                                            <Zap className="h-3 w-3" />
                                            <strong>Expedited: +${services.resume.find(s => s.name === 'Re-edit Resume' || s.id === 'reedit_resume')?.expedited_fee || 50}</strong>
                                        </p>
                                    </div>
                                    <ul className="text-xs text-muted-foreground space-y-1 mb-3">
                                        <li className="flex items-center gap-1">
                                            <Check className="h-3 w-3 text-green-500" />
                                            Change name
                                        </li>
                                        <li className="flex items-center gap-1">
                                            <Check className="h-3 w-3 text-green-500" />
                                            Add/remove info
                                        </li>
                                    </ul>
                                    <Button size="sm" className="w-full rounded-full" disabled>
                                        Order Now
                                    </Button>
                                </div>
                            )}
                        </div>
                        
                        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" />
                                <span>
                                    This preview reflects current service settings. Click <strong>"Save Changes"</strong> after editing a service to update the live site.
                                </span>
                            </p>
                        </div>
                        
                        <div className="flex justify-end gap-2 mt-4">
                            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
                                Close Preview
                            </Button>
                            <Button onClick={() => { setShowPreviewModal(false); window.open('/coaching', '_blank'); }} className="gap-2">
                                <ExternalLink className="h-4 w-4" />
                                Open Live Page
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
